// src/components/ProdMgrs/ProdMgrsCommissionsTable.jsx

import React from 'react';
import PropTypes from 'prop-types';

import DownloadIcon from '@mui/icons-material/Download';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axios from '../../../axiosClient.js';
import CommissionsRow from '../../CommissionsRow.jsx';
import { buildPmgrConfigMap, computePmgrLineCalcs } from '../utils/pmgrCommissionMath.js';
import ProdMgrsCommissionsHeader from './ProdMgrsCommissionsHeader.jsx';

function toNum(v) {
  if (v === null || v === undefined) {
    return 0;
  }
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : 0;
  }
  if (typeof v === 'string') {
    const cleaned = v.replace(/[$,]/g, '').trim();
    if (!cleaned) {
      return 0;
    }
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cmp(a, b) {
  if (a === b) {
    return 0;
  }
  if (a === null || a === undefined) {
    return -1;
  }
  if (b === null || b === undefined) {
    return 1;
  }
  return a < b ? -1 : 1;
}

function valueForSort(row, sortBy) {
  if (!row) {
    return null;
  }

  if (sortBy === 'InvoiceDate') {
    return String(row.InvoiceDate ?? '');
  }
  if (sortBy === 'InvoiceNo') {
    return String(row.InvoiceNo ?? '');
  }
  if (sortBy === 'ProductGroup') {
    return String(row.ProductGroup ?? row.Category ?? '');
  }
  if (sortBy === 'NetSale') {
    return toNum(row.NetSale);
  }
  if (sortBy === 'CommissionRate') {
    return toNum(row.CommissionRate);
  }
  if (sortBy === 'LineCommissions') {
    return toNum(row.LineCommissions ?? row.lineCommission);
  }

  return row[sortBy];
}

function sortRows(rows, sortBy, sortDir) {
  const dir = sortDir === 'desc' ? -1 : 1;
  const safe = Array.isArray(rows) ? rows : [];

  // Stable sort: decorate with original index
  const decorated = safe.map((r, i) => ({ r, i }));

  decorated.sort((x, y) => {
    const ax = valueForSort(x.r, sortBy);
    const ay = valueForSort(y.r, sortBy);

    const c = cmp(ax, ay);
    if (c !== 0) {
      return c * dir;
    }

    // tie-breaker keeps deterministic order
    return (x.i - y.i) * 1;
  });

  return decorated.map((d) => d.r);
}

// ============================================================================
// BLOCK PMGR.COMM.SEARCH.0 — Global client-side search (TrackingInfo-style)
// Notes:
//  - Converts dates into multiple tokens: M/D/YY, MM/DD/YYYY, YYYY-MM-DD, etc.
//  - Normalizes row into a single lowercase string for includes() matching
// ============================================================================

function toSearchableDateTokens(value) {
  if (!value) {
    return [];
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return [];
  }

  const month = d.getMonth() + 1;
  const day = d.getDate();
  const year2 = String(d.getFullYear()).slice(-2);
  const year4 = String(d.getFullYear());

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');

  return [
    `${month}/${day}/${year2}`,
    `${mm}/${dd}/${year2}`,
    `${month}/${day}/${year4}`,
    `${mm}/${dd}/${year4}`,
    `${year4}-${mm}-${dd}`,
  ];
}

function normalizeCommissionRowToSearchText(r) {
  // Add any date fields you want searchable here
  const dateTokens = [r?.InvoiceDate].flatMap(toSearchableDateTokens);

  // Keep this aligned with what you render / care about searching
  const parts = [
    r?.InvoiceNo,
    r?.InvoiceDate,

    r?.Customer,
    r?.CustomerName,
    r?.CustomerNo,

    r?.CustomerPONo,
    r?.PONo,

    r?.Tracking,
    r?.TrackingNo,
    r?.MstrTrackingNo,

    r?.State,
    r?.ZipCode,
    r?.Zip,
    r?.AreaCode,
    r?.City,

    r?.Rep,
    r?.RepName,
    r?.SalespersonNo,
    r?.SalespersonNo2,
    r?.SalespersonNo3,
    r?.SalespersonNo4,
    r?.Reps, // if your normalization builds a combined reps string somewhere

    r?.Item,
    r?.ProductGroup,
    r?.Category,

    r?.Quantity,
    r?.UnitPrice,
    r?.Sale,
    r?.StandardCost,
    r?.BaseCost,
    r?.ContractFee,
    r?.NetSale,

    r?.RunningTotal,
    r?.RunningNetTotal,

    r?.CommissionRate,
    r?.LineCommissions,
    r?.TotalCommission,

    ...dateTokens,
  ];

  return parts
    .map((v) => (v === null || v === undefined ? '' : String(v)))
    .join(' ')
    .toLowerCase();
}

function csvEscape(v) {
  const text = String(v ?? '');
  if (text.includes('"') || text.includes(',') || text.includes('\n') || text.includes('\r')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function toCsv(rows) {
  const headers = [
    'Invoice',
    'Invoice Date',
    'Customer',
    'Customer PO#',
    'Tracking',
    'State',
    'Area Code',
    'City',
    'Reps',
    'Item',
    'Product Category',
    'Quantity',
    'Unit Price',
    'Sale',
    'Base Cost',
    'Contract Fee',
    'Net Sale',
    'Running Total',
    'Running Net Total',
    '% Commission',
    'Commission',
    'Total Commission',
  ];

  const body = (Array.isArray(rows) ? rows : []).map((row) => {
    const values = [
      row?.InvoiceNo,
      row?.InvoiceDate,
      row?.Customer,
      row?.CustomerPONo,
      row?.Tracking,
      row?.State,
      row?.AreaCode,
      row?.City,
      row?.Reps,
      row?.Item,
      row?.ProductGroup ?? row?.Category,
      row?.Quantity,
      row?.UnitPrice,
      row?.Sale,
      row?.BaseCost,
      row?.ContractFee,
      row?.NetSale,
      row?.RunningTotal,
      row?.RunningNetTotal,
      row?.CommissionRate,
      row?.LineCommissions ?? row?.lineCommission,
      row?.TotalCommission,
    ];

    return values.map(csvEscape).join(',');
  });

  return [headers.map(csvEscape).join(','), ...body].join('\n');
}

function ProdMgrsCommissionsTable({ selectedRep, selectedYear, selectedMonth }) {
  const [rawLines, setRawLines] = React.useState([]);
  const [cfgMap, setCfgMap] = React.useState({});
  const [allCommissions, setAllCommissions] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [totalRows, setTotalRows] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  // Global search (client-side)
  const [searchText, setSearchText] = React.useState('');

  // Sorting state
  const [sortBy, setSortBy] = React.useState('InvoiceDate');
  const [sortDir, setSortDir] = React.useState('asc');

  const onSort = React.useCallback(
    (col) => {
      setPage(0);

      if (sortBy !== col) {
        setSortBy(col);
        setSortDir('asc');
        return;
      }

      setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
    },
    [sortBy]
  );

  const filteredRawLines = React.useMemo(() => {
    const q = String(searchText || '')
      .trim()
      .toLowerCase();
    if (!q) {
      return rawLines;
    }
    return (rawLines || []).filter((r) => normalizeCommissionRowToSearchText(r).includes(q));
  }, [rawLines, searchText]);

  // Recompute computed rows whenever rawLines/cfgMap/sort changes
  React.useEffect(() => {
    const sorted = sortRows(filteredRawLines, sortBy, sortDir);

    // IMPORTANT:
    // Running totals + tier decisions are computed in the order we pass in here.
    // By filtering BEFORE compute, the running totals align with what's displayed.
    const computed = computePmgrLineCalcs(sorted, cfgMap);

    setAllCommissions(computed);
    setTotal(computed.length);
  }, [filteredRawLines, cfgMap, sortBy, sortDir]);

  React.useEffect(() => {
    setPage(0);
  }, [searchText]);

  const pageRows = React.useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return allCommissions.slice(start, end);
  }, [allCommissions, page, rowsPerPage]);

  const canFetchMore = rawLines.length < totalRows;

  const handleFetchMore = React.useCallback(async () => {
    if (isLoading || loadingMore || !canFetchMore) {
      return;
    }

    setLoadingMore(true);
    setError(null);

    try {
      const monthParam =
        selectedMonth === undefined || selectedMonth === '' || selectedMonth === null
          ? null
          : Number(selectedMonth);

      const res = await axios.get('/api/prod-mgrs/programs', {
        params: {
          rep: selectedRep,
          year: selectedYear,
          month: monthParam,
          limit: 5000,
          offset: rawLines.length,
        },
      });

      const nextLines = (() => {
        if (Array.isArray(res?.data?.commissions)) {
          return res.data.commissions;
        }
        if (Array.isArray(res?.data?.programs)) {
          return res.data.programs;
        }
        if (Array.isArray(res?.data)) {
          return res.data;
        }
        return [];
      })();

      setRawLines((prev) => (nextLines.length ? [...prev, ...nextLines] : prev));

      if (Number.isFinite(res?.data?.total)) {
        setTotalRows(Number(res.data.total));
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoadingMore(false);
    }
  }, [
    canFetchMore,
    isLoading,
    loadingMore,
    rawLines.length,
    selectedMonth,
    selectedRep,
    selectedYear,
  ]);

  const handleDownloadCsv = React.useCallback(() => {
    const csvText = toCsv(allCommissions);
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const safeRep = String(selectedRep || 'rep')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const monthLabel =
      selectedMonth === undefined || selectedMonth === '' || selectedMonth === null
        ? 'all'
        : String(selectedMonth);
    const filename = `prod-mgrs-line-detail-${safeRep || 'rep'}-${selectedYear}-${monthLabel}.csv`;

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [allCommissions, selectedRep, selectedYear, selectedMonth]);

  React.useEffect(() => {
    if (!selectedRep || !selectedYear) {
      setRawLines([]);
      setCfgMap({});
      setAllCommissions([]);
      setTotal(0);
      setTotalRows(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    const monthParam =
      selectedMonth === undefined || selectedMonth === '' || selectedMonth === null
        ? null
        : Number(selectedMonth);

    Promise.all([
      axios.get('/api/prod-mgrs/quota-breakdown', {
        params: { rep: selectedRep, year: selectedYear },
      }),
      axios.get('/api/prod-mgrs/programs', {
        params: {
          rep: selectedRep,
          year: selectedYear,
          month: monthParam,
          limit: 5000,
          offset: 0,
        },
      }),
    ])
      .then(([cfgRes, linesRes]) => {
        const cfg = Array.isArray(cfgRes?.data) ? cfgRes.data : [];
        const map = buildPmgrConfigMap(cfg);

        let lines = [];

        if (Array.isArray(linesRes?.data?.commissions)) {
          lines = linesRes.data.commissions;
        } else if (Array.isArray(linesRes?.data?.programs)) {
          lines = linesRes.data.programs;
        } else if (Array.isArray(linesRes?.data)) {
          lines = linesRes.data;
        }

        const totalCount = Number.isFinite(linesRes?.data?.total)
          ? Number(linesRes.data.total)
          : lines.length;

        setCfgMap(map);
        setRawLines(lines);
        setTotalRows(totalCount);
      })
      .catch((e) => {
        setError(e);
        setRawLines([]);
        setCfgMap({});
        setAllCommissions([]);
        setTotal(0);
        setTotalRows(0);
      })
      .finally(() => setIsLoading(false));
  }, [selectedRep, selectedYear, selectedMonth]);

  React.useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  return (
    <Paper elevation={3} sx={{ padding: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">Program Manager Line Detail</Typography>

        {/* ================================================================= */}
        {/* BLOCK PMGR.COMM.SEARCH.1 — Global client-side search input         */}
        {/* ================================================================= */}
        <TextField
          size="small"
          label="Search"
          placeholder="Invoice, Customer, Item, City, State, Date (MM/DD/YY)…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 280 }}
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadCsv}
          disabled={!selectedRep || isLoading || allCommissions.length === 0}
          data-cy="export-prod-mgrs-line-detail-button"
          sx={{ height: '40px' }}
        >
          Export CSV
        </Button>
      </Stack>

      {!selectedRep ? (
        <Alert severity="info">Select a rep to view program manager commissions.</Alert>
      ) : null}

      {error ? <Alert severity="error">Failed to load program manager commissions.</Alert> : null}

      {!isLoading ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            onClick={handleFetchMore}
            disabled={loadingMore || !canFetchMore}
          >
            {loadingMore ? 'Fetching...' : 'Fetch Additional Records'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            Loaded {rawLines.length.toLocaleString()} of {totalRows.toLocaleString()} rows
          </Typography>
        </Stack>
      ) : null}

      {isLoading ? (
        <CircularProgress size={28} />
      ) : (
        <>
          <TableContainer>
            <Table size="small" stickyHeader>
              <ProdMgrsCommissionsHeader isRep sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <TableBody>
                {pageRows.map((commission) => (
                  <CommissionsRow key={commission.id} commission={commission} isRep />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </>
      )}
    </Paper>
  );
}

ProdMgrsCommissionsTable.propTypes = {
  selectedRep: PropTypes.string.isRequired,
  selectedYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  selectedMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ProdMgrsCommissionsTable;
